import { LitElement, html } from 'lit';
import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../shared/services/debug.service.js';
import { style } from './step-identity.style.js';
import { ApplicationService } from '../../../services/application.service.js';
import { globalStyle } from '../../../global-styles.style.js';
import './ssn/ssn.component.js';
import './dob/dob.component.js';
import { validateForm } from '../../../shared/decorators/validate-form.decorator.js';
import '@cof/fs-modal-dialog-wc/fs-modal-dialog.js';
import { stratum } from '../../../../constants/stratum.js';
import { SessionService } from '../../../core/services/session/session.service.js';
import { REFI_PERSONAL_CONST } from '../../../shared/constants/app.constants.js';

class RefiStepIdentityComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepIdentity');
    this.validity = {};
    this.pending = false;
    this.childIds = {
      ssn: 'ssnInput',
      dob: 'dateOfBirthInput',
    };
  }

  static properties = {
    application: { type: Object, reflect: true },
    applicant: { type: Object, reflect: true },
    isPrefill: { type: Boolean, attribute: 'is-prefill' },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    applicantType: { type: String, attribute: 'applicant-type', reflect: true },
  };

  static styles = [style, globalStyle];

  get application() {
    return ApplicationService.Application;
  }

  get applicant() {
    return this.isPrimary
      ? ApplicationService.Application.primaryApplicant
      : ApplicationService.Application.coApplicant;
  }

  connectedCallback() {
    super.connectedCallback();
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.applicantType = this.isPrimary
      ? REFI_PERSONAL_CONST.APPLICANT_TYPE.PRIMARY_APPLICANT
      : REFI_PERSONAL_CONST.APPLICANT_TYPE.CO_APPLICANT;
    stratum.publishTag(
      this.isPrimary
        ? TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_IDENTITY
        : TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_CO_APP_IDENTITY
    );
  }

  render() {
    return html`
      <form
          id="step-identity"
          class="step-identity"
          @submit="${this.onSubmit}"
          novalidate
      >
        <refi-heading>
          ${
            this.isPrimary
              ? html` ${this.isPrefill
                  ? html`${this.applicant.firstName}, please confirm your
                    identity`
                  : html`${this.applicant.firstName}, help us verify your
                    identity`}`
              : html` Help us verify ${this.applicant.firstName}’s identity`
          }
        </refi-heading>

        <refi-personal-ssn
            id="${this.childIds.ssn}"
            @validity="${this.updateValidity}"
            @pending="${this.updatePending}"
            ?is-primary="${this.isPrimary}"
        ></refi-personal-ssn>

        <refi-personal-dob
            id="${this.childIds.dob}"    
            @validity="${this.updateValidity}"
            ?is-primary="${this.isPrimary}"
        ></refi-personal-dob>

        <div class="button-container">
          <fs-button class="fs-button next-button" variant="primary"
            >
            <span>${!this.isPrefill ? 'Next' : 'Confirm'}</span>
          </fs-button>
        </div>
      </form>
      
      <fs-modal-dialog accessible-title="ssnMismatchModal" id="ssnMismatchModal" dismissible size="small">
        <refi-heading slot="modal-dialog-header" id="mismatchModalHeader">
          Hmm, that Social Security number doesn't match what we verified with your access code.
        </refi-heading>
        <section class="fs-body-text fs-body-text--normal" slot="modal-dialog-body">
          <fs-body-text size="normal" variant="primary">
            In order to receive your personalized offers, the last four digits of the Social Security number you
            provided needs to match the information entered with your personal details.
          </fs-body-text>
        </section>
        <fs-modal-dialog-footer slot="modal-dialog-footer">
          <fs-button variant="primary" type="button" @click="${
            this.removeOfferAndContinue
          }">
            Continue without offer
          </fs-button>
          <fs-button variant="secondary" type="button" outlined='' @click="${
            this.closeSsnMismatchModal
          }">
            Edit your details
          </fs-button>
      </fs-modal-dialog>
    `;
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
  }

  updatePending(ev) {
    this.pending = ev.detail;
  }

  removeOfferAndContinue() {
    this.debug.log('removing access code offer');
    SessionService.removeCachedOffer();

    ApplicationService.Application = {
      ...ApplicationService.Application,
      promoCode: null,
    };

    stratum.publishTag(
      TagKey.PERSONAL_IDENTITY_SSN_NO_MATCH_CONTINUE_WITHOUT_OFFER
    );
    this.saveInfoToApplicant();
    this.dispatchEvent(new CustomEvent('next-step'));
  }

  closeSsnMismatchModal() {
    stratum.publishTag(TagKey.PERSONAL_IDENTITY_SSN_NO_MATCH_EDIT_DETAILS);
    this.shadowRoot.getElementById('ssnMismatchModal').closeModalDialog();
  }

  @validateForm()
  async onSubmit() {
    if (this.pending) return;

    this.inputChildren = {
      ssn: this.shadowRoot.getElementById(this.childIds.ssn),
      dob: this.shadowRoot.getElementById(this.childIds.dob),
    };
    this.runChildrenBusinessChecks().then(() => {
      let valid = true;
      this.debug.log(this.validity);
      Object.values(this.validity).forEach((validityValue) => {
        if (!validityValue) {
          valid = false;
        }
      });

      if (valid) {
        // offer code ssn matches applicant ssn check
        const applicantLastFour =
          this.inputChildren.ssn.values.ssn.substring(7);
        const offerLastFour = SessionService.getCachedOffer()?.lastFourTaxId;
        if (
          offerLastFour &&
          this.isPrimary &&
          applicantLastFour !== offerLastFour
        ) {
          trackAnalyticsEvent('access code ssn no match');
          stratum.publishTag(TagKey.PERSONAL_IDENTITY_ACCESS_CODE_SSN_NO_MATCH);
          this.shadowRoot.getElementById('ssnMismatchModal').openModalDialog();
        } else {
          this.saveInfoToApplicant();
          this.debug.log('valid', this.application);
          this.dispatchEvent(new CustomEvent('next-step'));
        }
      }
    });
  }

  saveInfoToApplicant() {
    this.oldApplicantInfo = structuredClone(this.applicant);

    // Collect all updates
    const applicantUpdates = {};
    Object.values(this.inputChildren).forEach((child) => {
      Object.keys(child.values).forEach((field) => {
        applicantUpdates[field] = child.values[field];
      });
    });

    ApplicationService.Application = {
      ...ApplicationService.Application,
      [this.applicantType]: {
        ...ApplicationService.Application[this.applicantType],
        ...applicantUpdates,
      },
    };

    this.tagFieldChangesOnSubmit();
  }

  async runChildrenBusinessChecks() {
    return this.inputChildren.ssn.runBusinessChecks().then(() => {
      this.inputChildren.dob.runBusinessChecks();
      return Promise.resolve();
    });
  }

  tagFieldChangesOnSubmit() {
    Object.keys(this.applicant).forEach((field) => {
      if (
        typeof this.applicant[field] !== 'object' &&
        this.applicant[field] !== this.oldApplicantInfo[field]
      ) {
        this.debug.log(`${this.coAppPrefix}${field}`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `${this.coAppPrefix}${field}`,
          },
        });
      }
    });
  }
}

customElements.define('refi-step-identity', RefiStepIdentityComponent);
